import { useEffect, useMemo } from "react";

import { UnitTypes } from "@/constants/bodyMetrics";
import { manBodyPicByBodyId } from "@/constants/bodyPicByBodyId";
import {
    BodyTypes,
    funnelUpgradeMapBodyTypesToBackend,
    funnelUpgradeMapTargetBodyTypesToBackend,
} from "@/constants/bodyType";
import { QUESTIONS_IDS } from "@/constants/questionsIds";
import { Routes as AppRoutes } from "@/constants/routes";
import { Answers, TARGET_WEIGHT_STRING_KEY } from "@/utils/AnswersProvider";
import { getFunnelFoxUrlParams } from "@/utils/funnelFoxHelpers";
import { useGender } from "@/utils/GenderProvider";
import { calculateWeight } from "@/utils/services/weightFormatters";
import { UrlParams } from "@/utils/urlParams";

const QUESTIONS_IDS_FROM_URL = [
    QUESTIONS_IDS.BODY_FAT,
    QUESTIONS_IDS.BODY_TYPE, // in backend body type value
    QUESTIONS_IDS.DESIRED_BODY, // in backend body type value
    QUESTIONS_IDS.PRIMARY_GOAL,
    QUESTIONS_IDS.FITNESS_LEVEL,
    UrlParams.PREFERRED_ENVIRONMENT,
    QUESTIONS_IDS.TARGET_WEIGHT, // always in kgs by default
    QUESTIONS_IDS.UNITS,
];

const useFunnelFoxAnswers = ({
    setEmail,
    setAnswers,
    setName,
}: {
    setEmail: (email: string) => void;
    setAnswers: (value: React.SetStateAction<Answers>) => void;
    setName: (name: string) => void;
}) => {
    const { isFemale } = useGender();

    const isFunnelFoxLink = useMemo(
        () => window.location.pathname.includes(AppRoutes.FFPaywall),
        []
    );

    const setEmailFromUrl = (urlParams: URLSearchParams) => {
        const emailFromUrl = urlParams.get(UrlParams.EMAIL);

        if (emailFromUrl) {
            setEmail(emailFromUrl);
        }
    };

    const setNameFromUrl = (urlParams: URLSearchParams) => {
        const nameFromUrl = urlParams.get(UrlParams.NAME);

        if (nameFromUrl) {
            setName(nameFromUrl);
        }
    };

    const setAnswersFromUrl = (urlParams: URLSearchParams) => {
        const answersFromUrl = QUESTIONS_IDS_FROM_URL.reduce((acc, id) => {
            let questionId = id;
            const answer = urlParams.get(id);
            let answerValue = answer;

            if (id === UrlParams.PREFERRED_ENVIRONMENT) {
                questionId = QUESTIONS_IDS.LOCATION;
            } else if (id === QUESTIONS_IDS.BODY_TYPE) {
                const bodyType =
                    Object.entries(funnelUpgradeMapBodyTypesToBackend).find(
                        ([_, value]) => value === answer
                    )?.[0] ?? BodyTypes.MidSize;
                answerValue = bodyType;
            } else if (id === QUESTIONS_IDS.DESIRED_BODY) {
                if (isFemale) {
                    const desiredBody =
                        Object.entries(funnelUpgradeMapTargetBodyTypesToBackend).find(
                            ([_, value]) => value === answer
                        )?.[0] ?? BodyTypes.Toned;
                    answerValue = desiredBody;
                } else {
                    answerValue =
                        Object.keys(manBodyPicByBodyId).find((key) => key === answer) ??
                        BodyTypes.Muscular;
                }
            } else if (id === QUESTIONS_IDS.TARGET_WEIGHT) {
                const units = urlParams.get(QUESTIONS_IDS.UNITS) as UnitTypes;
                const weightStr = calculateWeight(Number(answer), units);
                acc = { ...acc, [TARGET_WEIGHT_STRING_KEY]: weightStr };
            }

            if (answer) {
                return { ...acc, [questionId]: answerValue };
            }
            return acc;
        }, {});

        if (Object.keys(answersFromUrl).length > 0) {
            setAnswers((prev) => ({
                ...prev,
                ...answersFromUrl,
            }));
        }
    };

    useEffect(() => {
        if (isFunnelFoxLink) {
            const urlParams = getFunnelFoxUrlParams();

            setEmailFromUrl(urlParams);
            setNameFromUrl(urlParams);
            setAnswersFromUrl(urlParams);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFunnelFoxLink]);

    return { isFunnelFoxLink };
};

export default useFunnelFoxAnswers;
